import React from 'react'
// libs
import { useSelector, useDispatch } from 'react-redux'
import { change, formValueSelector, isDirty, reset } from 'redux-form'
// hooks
import { useModalData } from 'hooks/index'
// context
import { KPIContext } from 'context/index'
// components
import { Modal } from 'components/UI'
import RecurrenceForm from './Form'
// constants
import {
  Forms,
  Modals,
  REPEAT_VALUES,
  REPEAT_OPTIONS,
  CAPTURE_TYPE_OPTIONS,
  RECURRENCE_ENDS_VALUES,
  EACH_PERIOD_ENDS_TYPE_OPTIONS
} from 'constants/index'
// utils
import get from 'lodash/fp/get'
import moment from 'moment'
import isEmpty from 'lodash/isEmpty'
import { trimTz } from 'utils/date'
import { handleError } from 'utils/errors/handlers'
import { generateRecurrenceLabel } from 'utils/index'
import { getFieldId, getValueFromOption, getOptionByValue } from 'utils/form'
import { generateKPIRecurrenceRanges, normalizeRecurrenceData } from 'utils/kpi'

const PAPER_PROPS = {
  style: {
    margin: 0,
    overflow: 'auto'
  }
}

const currentFormSelector = formValueSelector(Forms.KPI_RECURRENCE_FORM)

interface Props {
  targetForm: string
  targetField: string
  displayField: string
}

const KPIRecurrenceFormModal: React.FC<Props> = ({ targetForm, targetField, displayField }) => {
  const dispatch = useDispatch()

  const {
    hideModal,
    modalData: { isOpen, isCreateForm }
  } = useModalData({ modalType: Modals.KPI_RECURRENCE_MODAL })

  const [isLoading, setLoading] = React.useState(false)

  const { entity, update } = React.useContext<any>(KPIContext)

  const detailsFormSelector = formValueSelector(targetForm)

  const {
    days,
    eachPeriodEndsType,
    firstPeriodStartsAt,
    isCurrentFormDirty,
    isFormDirty,
    recurrence,
    repeatEveryType,
    repeatEveryValue,
    resultTarget
  } = useSelector((state) => ({
    days: currentFormSelector(state, `days`),
    eachPeriodEndsType: currentFormSelector(state, `eachPeriodEndsType`),
    firstPeriodStartsAt: currentFormSelector(state, `firstPeriodStartsAt`),
    isCurrentFormDirty: isDirty(Forms.KPI_RECURRENCE_FORM)(state),
    isFormDirty: isDirty(targetForm)(state),
    recurrence: detailsFormSelector(state, 'recurrence'),
    repeatEveryType: currentFormSelector(state, `repeatEveryType`) || {},
    repeatEveryValue: currentFormSelector(state, `repeatEveryValue`),
    resultTarget: detailsFormSelector(state, 'resultTarget')
  }))

  // methods
  const changeField = (field, value, form) => dispatch(change(form, field, value))

  const recalculateDates = () => {
    if (isCurrentFormDirty) {
      const startPoint = moment(firstPeriodStartsAt)

      if (!firstPeriodStartsAt) {
        changeField(`firstPeriodStartsAt`, trimTz(moment().startOf('d')), Forms.KPI_RECURRENCE_FORM)

        return
      }

      let shouldCalculate = false

      if (repeatEveryType.value === REPEAT_VALUES.WEEKLY) {
        shouldCalculate = Array.isArray(days) && !!days.length
      }

      if (repeatEveryType.value === REPEAT_VALUES.MONTHLY) {
        shouldCalculate = !!eachPeriodEndsType
      }

      if (shouldCalculate) {
        const { lastDayOfThePeriod } = generateKPIRecurrenceRanges(
          { repeatEveryValue, repeatEveryType, days, eachPeriodEndsType },
          startPoint
        )

        changeField(`firstPeriodEndsAt`, trimTz(lastDayOfThePeriod.startOf('d')), Forms.KPI_RECURRENCE_FORM)
      }
    }
  }

  const handleUpdate = (data) => {
    changeField(
      targetField,
      {
        ...data,
        repeatEveryType: getValueFromOption(data.repeatEveryType),
        eachPeriodEndsType: getValueFromOption(data.eachPeriodEndsType),
        captureType: getValueFromOption(data.captureType),
        recurrenceEndsAt: data.recurrenceEnds === RECURRENCE_ENDS_VALUES.ON ? data.recurrenceEndsAt : null
      },
      targetForm
    )

    changeField(displayField, generateRecurrenceLabel(normalizeRecurrenceData(data)), targetForm)

    hideModal()
  }

  const handleSubmit = async (data) => {
    const isPeriodValueExist = (id) => resultTarget.capturedResults.some((capturedResult) => capturedResult.id === id)

    const getUpdatedResultsTarget = () => {
      if (get('capturedResults', resultTarget) && get('differentResults', resultTarget)) {
        const differentResults = resultTarget.differentResults
          .filter(({ id, results }) => !isEmpty(results) || isPeriodValueExist(id))
          .map((period) => {
            if (isPeriodValueExist(period.id)) {
              return { ...period, withStoredData: true }
            }

            return period
          })

        return { ...resultTarget, differentResults }
      }

      return resultTarget
    }

    try {
      setLoading(true)

      const recurrenceData = {
        ...data,
        repeatEveryType: getValueFromOption(data.repeatEveryType),
        eachPeriodEndsType: getValueFromOption(data.eachPeriodEndsType),
        captureType: getValueFromOption(data.captureType),
        recurrenceEndsAt: data.recurrenceEnds === RECURRENCE_ENDS_VALUES.ON ? data.recurrenceEndsAt : null
      }

      const resultTargetData = getUpdatedResultsTarget()

      await update({
        variables: {
          id: entity.id,
          input: {
            fieldValues: [
              { fieldId: getFieldId(entity.fields, 'recurrence'), value: recurrenceData },
              { fieldId: getFieldId(entity.fields, 'resultTarget'), value: resultTargetData }
            ],
            recurrence: recurrenceData,
            resultTarget: resultTargetData
          }
        }
      })

      dispatch(reset(targetForm))

      hideModal()
    } catch (error) {
      handleError(error, 'KPIRecurrenceFormModal.handleSubmit')
    } finally {
      setLoading(false)
    }
  }

  const handleFormSubmit = isCreateForm || isFormDirty ? handleUpdate : handleSubmit

  // constants
  const formattedFirstPeriod = firstPeriodStartsAt ? moment(firstPeriodStartsAt).format('L') : null

  const initialValues = React.useMemo(() => {
    if (recurrence) {
      return {
        ...recurrence,
        repeatEveryType: getOptionByValue(recurrence.repeatEveryType, REPEAT_OPTIONS),
        eachPeriodEndsType: getOptionByValue(recurrence.eachPeriodEndsType, EACH_PERIOD_ENDS_TYPE_OPTIONS),
        captureType: getOptionByValue(recurrence.captureType, CAPTURE_TYPE_OPTIONS)
      }
    }
  }, [recurrence])

  React.useEffect(recalculateDates, [days, eachPeriodEndsType, formattedFirstPeriod, repeatEveryType, repeatEveryValue])

  return (
    <Modal width={'xs'} isOpen={isOpen} disableRestoreFocus PaperProps={PAPER_PROPS}>
      <RecurrenceForm
        hideModal={hideModal}
        isLoading={isLoading}
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        repeatEveryType={repeatEveryType.value}
      />
    </Modal>
  )
}

export default KPIRecurrenceFormModal
